import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {
  Aangifte,
  BokkzIbDataService,
  IncomeTaxEntity,
  LoonUitTegenwoordigeDienstbetrekking,
} from '@snelstart-ventures/fiscaal/domain-logic';
import { debounceTime, filter, map, Observable, of, pairwise, shareReplay, startWith, Subscription, switchMap, take, tap } from 'rxjs';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {CommonModule} from '@angular/common';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

type LoonFormGroup = FormGroup<{
  loon: OmschrijvingEnBedragFormGroup;
  loonheffing: FormControl<number | null>;
  arbeidskorting: FormControl<number | null>;
  onbelastDeelLoonInNederland: FormControl<number | null>;
  loonsoort: FormControl<string | null>;
}>;

type OmschrijvingEnBedragFormGroup = FormGroup<{
  omschrijving: FormControl<string | null>;
  bedrag: FormControl<number | null>;
}>;

@Component({
    selector: 'snelstart-venture-fiscaal-feature-loon-tegenwoordig',
    templateUrl: './loon-tegenwoordig.component.html',
    styleUrls: ['./loon-tegenwoordig.component.scss'],
    imports: [SharedUiDetailsModule, SharedUiFormControlsModule, SharedUiButtonModule, CommonModule, SharedUiAlertModule],
})
export class LoonTegenwoordigComponent implements OnInit, OnDestroy {
    @Input() public id!: string;
    private dataService = inject(BokkzIbDataService);
    private destroyRef = inject(DestroyRef);
    private changeSubscriptions = new Subscription();
    public dialogVisible = false;
    public heeftGeenOuderenkorting = false;
    public index = 0;
    public isPartner = false;
    public isDefAangifte = false;

    readonly incomeTaxEntity$: Observable<IncomeTaxEntity> = this.dataService.incomeTaxEntity$.pipe(
        filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public formGroup = new FormGroup({
        loonTegenwoordigeArbeid: new FormArray<LoonFormGroup>([]),
    });

    public wajongFg = new FormGroup({
        krijgtWajong: new FormControl<boolean | null>(false),
    });

    public optionsJaNee: SelectFormControlItem<boolean | undefined>[] = [
        { value: false, label: 'Nee, geen wajong-korting' },
        { value: true, label: 'Ja, wel wajong-korting' },
    ];

    ngOnInit(): void {
        this.dataService.partnerAangifte$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((partner) => {
            this.isPartner = partner;
            this.incomeTaxEntity$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((entity) => {
                this.isDefAangifte = entity.taxType === 'DA';
                if (partner) {
                    this.handleSwitch(entity, true);
                } else {
                    this.handleSwitch(entity, false);
                }
            });
        });
        this.subscribeToChanges();
    }

    ngOnDestroy(): void {
        this.changeSubscriptions.unsubscribe();
    }

    private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
        const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
        if (details) {
            this.resetAndPatchForms(details);
            this.checkOuderenkorting(entity, isPartner);
        }
    }

    private resetAndPatchForms(details: any): void {
        this.formGroup.reset({}, { emitEvent: false }); // Prevent reset from emitting events
        this.wajongFg.reset({}, { emitEvent: false }); // Prevent reset from emitting events
        this.updateForm(details.box1?.belastbaarLoon?.loonUitTegenwoordigeDienstbetrekking || []);
        this.wajongFg.patchValue({ krijgtWajong: details.heffingskortingen?.krijgtWajong ?? false }, { emitEvent: false });
    }

    private subscribeToChanges(): void {
        this.changeSubscriptions.unsubscribe();
        this.changeSubscriptions = new Subscription();

        const extractInputForCalculation = () =>
            this.loonTegenwoordigeArbeid.getRawValue().map((item) => ({
                bedrag: item.loon.bedrag,
                loonheffing: item.loonheffing,
                onbelastDeelLoonInNederland: item.onbelastDeelLoonInNederland,
            }));

        this.changeSubscriptions.add(
            this.loonTegenwoordigeArbeid.valueChanges
                .pipe(
                    debounceTime(500),
                    tap(() => this.saveForm()),
                    map(() => extractInputForCalculation()),
                    startWith(extractInputForCalculation()),
                    pairwise(),
                    filter(([prev, curr]) => JSON.stringify(prev) !== JSON.stringify(curr)),
                    switchMap(() => (this.isDefAangifte ? of({} as Aangifte) : this.dataService.calculateLoonheffingen())),
                    switchMap(() => this.dataService.calculateLatestIncomeTaxReturn()),
                    takeUntilDestroyed(this.destroyRef),
                )
                .subscribe(),
        );
    }

    get loonTegenwoordigeArbeid(): FormArray<LoonFormGroup> {
        return this.formGroup.controls.loonTegenwoordigeArbeid;
    }

    addRij(): void {
        this.loonTegenwoordigeArbeid.push(this.createFormGroup(), { emitEvent: false });
    }

    verwijderRij(index: number): void {
        this.loonTegenwoordigeArbeid.removeAt(index, { emitEvent: false });
        this.formGroup.updateValueAndValidity({ emitEvent: false });
        this.formGroup.markAsDirty({ emitEvent: false });
        this.saveForm();
        this.subscribeToChanges();
    }

    createFormGroup(item?: LoonUitTegenwoordigeDienstbetrekking): FormGroup {
        return new FormGroup({
            loon: new FormGroup({
                omschrijving: new FormControl<string | null>(item?.loon?.omschrijving ?? null),
                bedrag: new FormControl<number | null>(item?.loon?.bedrag ?? null),
            }),
            loonheffing: new FormControl<number | null>(item?.loonheffing ?? null),
            arbeidskorting: new FormControl<number | null>(item?.arbeidskorting ?? null),
            onbelastDeelLoonInNederland: new FormControl<number | null>(item?.onbelastDeelLoonInNederland ?? null),
            loonsoort: new FormControl<string | null>(item?.loonsoort ?? null),
        });
    }

    private updateForm(loon: LoonUitTegenwoordigeDienstbetrekking[]): void {
        const lijst = loon || [];
        const formArray = this.loonTegenwoordigeArbeid;

        while (formArray.length > lijst.length) {
            formArray.removeAt(formArray.length - 1, { emitEvent: false });
        }

        for (let i = 0; i < lijst.length; i++) {
            if (i < formArray.length) {
                const formGroup = formArray.at(i) as FormGroup;
                formGroup.patchValue(lijst[i], { emitEvent: false });
            } else {
                const formGroup = this.createFormGroup(lijst[i]);
                formArray.push(formGroup, { emitEvent: false });
            }
        }
    }

    saveForm(): void {
        this.formGroup.updateValueAndValidity({ emitEvent: false });
        this.wajongFg.updateValueAndValidity({ emitEvent: false });

        this.dataService.patchIncomeTaxEntity((currentEntity) => {
            if (!currentEntity.aangifte) {
                return currentEntity;
            }

            const update = (aangifte: Aangifte) => ({
                ...aangifte,
                box1: {
                    ...aangifte.box1,
                    belastbaarLoon: {
                        ...aangifte.box1?.belastbaarLoon,
                        loonUitTegenwoordigeDienstbetrekking: this.formGroup.getRawValue().loonTegenwoordigeArbeid,
                    },
                },
                heffingskortingen: {
                    ...aangifte.heffingskortingen,
                    krijgtWajong: this.wajongFg.getRawValue().krijgtWajong,
                },
            });

            const updatedAangifte = this.isPartner
                ? {
                      ...currentEntity.aangifte,
                      partnerAangifte: update(currentEntity.aangifte?.partnerAangifte!),
                  }
                : update(currentEntity.aangifte);

            return {
                ...currentEntity,
                aangifte: updatedAangifte,
            } as IncomeTaxEntity;
        });
    }

    public checkOuderenkorting(incomeTaxEntity: IncomeTaxEntity, partner: boolean) {
        const aangifte = incomeTaxEntity.aangifte!;
        let geboorteDatum = new Date();

        if (partner && aangifte?.partnerAangifte) {
            // Gebruik de geboortedatum van de partner als partnerBoolean waar is
            geboorteDatum = new Date(aangifte.partnerAangifte.aangever?.geboortedatum);
        } else if (aangifte?.aangever?.geboortedatum) {
            // Anders, gebruik de geboortedatum van de aangever
            geboorteDatum = new Date(aangifte.aangever.geboortedatum);
        }

        const aangifteJaar = incomeTaxEntity.year!;
        const AOWdatum = new Date(aangifteJaar - 67, 11, 31);

        this.heeftGeenOuderenkorting =
            geboorteDatum.getFullYear() > AOWdatum.getFullYear() ||
            (geboorteDatum.getFullYear() === AOWdatum.getFullYear() && geboorteDatum.getMonth() > AOWdatum.getMonth()) ||
            (geboorteDatum.getFullYear() === AOWdatum.getFullYear() &&
                geboorteDatum.getMonth() === AOWdatum.getMonth() &&
                geboorteDatum.getDate() > AOWdatum.getDate());
    }

    omschrijvingTeVeelKarakters(index: number): boolean {
        return this.loonTegenwoordigeArbeid.at(index).get('loon.omschrijving')?.hasError('maxlength') ?? false;
    }
}
